import { useState, useEffect, useCallback } from "react";

type UseOtherOptionParams = {
   otherEnabled: boolean;
   hasOptions: boolean;
   options: string[];
   selectedValues: string[];
   setValue: (value: unknown) => void;
   mode: "single" | "multi";
};

export const useOtherOption = ({
   otherEnabled,
   hasOptions,
   options,
   selectedValues,
   setValue,
   mode,
}: UseOtherOptionParams) => {
   const [isOtherSelected, setIsOtherSelected] = useState(false);
   const [otherText, setOtherText] = useState("");

   useEffect(() => {
      if (!otherEnabled || !hasOptions) return;

      if (mode === "single") {
         const currentValue = selectedValues[0] ?? "";
         const isOptionSelected = options.includes(currentValue);

         if (isOptionSelected) {
            setIsOtherSelected(false);
            setOtherText("");
            return;
         }

         if (currentValue === "") {
            if (!isOtherSelected) setOtherText("");
            return;
         }

         setIsOtherSelected(true);
         setOtherText(currentValue);
      } else {
         const otherValue = selectedValues.find(
            (item) => typeof item === "string" && !options.includes(item),
         );
         const checked = otherValue !== undefined;
         setIsOtherSelected(checked);
         setOtherText(typeof otherValue === "string" ? otherValue : "");
      }
   }, [otherEnabled, hasOptions, options, selectedValues, mode, isOtherSelected]);

   const selectOther = useCallback(() => {
      setIsOtherSelected(true);
      if (mode === "single") {
         setValue(otherText || "");
      } else {
         const withoutOther = selectedValues.filter(
            (item) => typeof item !== "string" || options.includes(item),
         );
         setValue([...withoutOther, otherText || ""]);
      }
   }, [mode, otherText, selectedValues, options, setValue]);

   const deselectOther = useCallback(() => {
      setIsOtherSelected(false);
      setOtherText("");
      if (mode === "multi") {
         const withoutOther = selectedValues.filter(
            (item) => typeof item !== "string" || options.includes(item),
         );
         setValue(withoutOther);
      }
   }, [mode, selectedValues, options, setValue]);

   const toggleOther = useCallback(
      (checked: boolean) => {
         if (checked) {
            selectOther();
         } else {
            deselectOther();
         }
      },
      [selectOther, deselectOther],
   );

   const handleOtherTextChange = useCallback(
      (text: string) => {
         setOtherText(text);
         if (mode === "single") {
            setValue(text);
         } else {
            const withoutOther = selectedValues.filter(
               (item) => typeof item !== "string" || options.includes(item),
            );
            setValue([...withoutOther, text]);
         }
      },
      [mode, selectedValues, options, setValue],
   );

   return {
      isOtherSelected,
      otherText,
      toggleOther,
      handleOtherTextChange,
   };
};
